import {
  AgentKey,
  AgentMetadata,
  ExperimentPage,
  ExperimentProduct,
  PageVisibilityCounts,
  ProductComparison,
} from './agentic-ecommerce.types';

export const AGENTS: Record<AgentKey, AgentMetadata> = {
  openai: {
    label: 'ChatGPT',
    provider: 'OpenAI',
    icon: 'radio_button_checked',
    brandColor: '#20c997',
  },
  claude: {
    label: 'Claude',
    provider: 'Anthropic',
    icon: 'auto_awesome',
    brandColor: '#f9735f',
  },
  perplexity: {
    label: 'Perplexity',
    provider: 'Perplexity AI',
    icon: 'fork_right',
    brandColor: '#22d3ee',
  },
  gemini: {
    label: 'Gemini',
    provider: 'Google',
    icon: 'diamond',
    brandColor: '#5b8cff',
  },
};

export function getAgentMetadata(agentKey: AgentKey): AgentMetadata {
  const knownAgent = AGENTS[agentKey];

  if (knownAgent) {
    return knownAgent;
  }

  return {
    label: toTitleCase(agentKey),
    provider: 'AI Agent',
    icon: 'smart_toy',
    brandColor: '#94a3b8',
  };
}

export function calculateVisibilityScore(
  totalMatchedProducts?: number,
  totalProducts?: number,
): number {
  const matched = Number(totalMatchedProducts || 0);
  const total = Number(totalProducts || 0);

  if (!Number.isFinite(matched) || !Number.isFinite(total) || total <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, Math.round((matched / total) * 100)));
}

export function getScoreColor(score: number): string {
  if (score >= 80) {
    return '#5fcf97';
  }

  if (score >= 50) {
    return '#f2b45f';
  }

  return '#ff756b';
}

export function getProducts(page?: ExperimentPage | null): ExperimentProduct[] {
  return Array.isArray(page?.products) ? page?.products || [] : [];
}

export function isProductVisible(
  product: ExperimentProduct,
  agentKey: AgentKey,
): boolean {
  return product?.seenBy?.[agentKey] === true;
}

export function isPdpPageType(pageType?: string): boolean {
  return String(pageType || '').toLowerCase() === 'pdp';
}

export function getProductComparison(
  product: ExperimentProduct | null,
  agentKey: AgentKey,
): ProductComparison | null {
  return product?.comparison?.[agentKey] || null;
}

export function isProductVisibleForPage(
  product: ExperimentProduct,
  agentKey: AgentKey,
  pageType?: string,
): boolean {
  if (isPdpPageType(pageType)) {
    return getProductComparison(product, agentKey)?.thresholdMet === true;
  }

  return isProductVisible(product, agentKey);
}

export function isProductMissingForPage(
  product: ExperimentProduct,
  agentKey: AgentKey,
  pageType?: string,
): boolean {
  if (isPdpPageType(pageType)) {
    const comparison = getProductComparison(product, agentKey);

    return (
      !comparison ||
      comparison.thresholdMet !== true ||
      getMissingComparisonFields(product, agentKey).length > 0
    );
  }

  return !isProductVisible(product, agentKey);
}

export function getPageVisibilityCounts(
  page: ExperimentPage | null | undefined,
  agentKey: AgentKey,
): PageVisibilityCounts {
  const products = getProducts(page);
  const visibleCount = products.filter((product) =>
    isProductVisibleForPage(product, agentKey, page?.pageType),
  ).length;
  const missingCount = products.filter((product) =>
    isProductMissingForPage(product, agentKey, page?.pageType),
  ).length;

  return {
    visibleCount,
    missingCount,
    totalCount: products.length,
  };
}

export function formatProductPrice(product: ExperimentProduct | null): string {
  const price = product?.price;
  const currency = String(product?.currency || '').trim();

  if (price === null || price === undefined || price === '') {
    return '';
  }

  const rawPrice = String(price).trim();

  if (!rawPrice) {
    return '';
  }

  if (
    currency &&
    typeof price === 'string' &&
    rawPrice.toLowerCase().includes(currency.toLowerCase())
  ) {
    return rawPrice;
  }

  const numericPrice =
    typeof price === 'number' ? price : parseLocalizedPrice(rawPrice);

  if (
    currency &&
    numericPrice !== null &&
    Number.isFinite(numericPrice)
  ) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format(numericPrice);
    } catch (e) {
      return `${currency} ${rawPrice}`;
    }
  }

  if (currency) {
    return `${currency} ${rawPrice}`;
  }

  return rawPrice;
}

function parseLocalizedPrice(price: string): number | null {
  const normalizedPrice = normalizeLocalizedPrice(price);

  if (!normalizedPrice) {
    return null;
  }

  const numericPrice = Number(normalizedPrice);

  return Number.isFinite(numericPrice) ? numericPrice : null;
}

function normalizeLocalizedPrice(price: string): string {
  const compactPrice = price.replace(/\s/g, '').replace(/[^\d,.-]/g, '');

  if (!/\d/.test(compactPrice)) {
    return '';
  }

  const lastCommaIndex = compactPrice.lastIndexOf(',');
  const lastDotIndex = compactPrice.lastIndexOf('.');

  if (lastCommaIndex >= 0 && lastDotIndex >= 0) {
    const decimalSeparator = lastCommaIndex > lastDotIndex ? ',' : '.';
    const groupSeparator = decimalSeparator === ',' ? '.' : ',';

    return removeNonLeadingMinus(
      compactPrice
        .replace(new RegExp(`\\${groupSeparator}`, 'g'), '')
        .replace(decimalSeparator, '.'),
    );
  }

  if (lastCommaIndex >= 0) {
    return normalizeSingleSeparatorPrice(compactPrice, ',');
  }

  if (lastDotIndex >= 0) {
    return normalizeSingleSeparatorPrice(compactPrice, '.');
  }

  return removeNonLeadingMinus(compactPrice);
}

function normalizeSingleSeparatorPrice(
  price: string,
  separator: ',' | '.',
): string {
  const parts = price.split(separator);
  const lastPart = parts[parts.length - 1] || '';
  const hasDecimalPart = /^\d{1,2}$/.test(lastPart);
  const integerParts = hasDecimalPart ? parts.slice(0, -1) : parts;
  const integerValue = removeNonLeadingMinus(integerParts.join(''));

  if (hasDecimalPart) {
    return `${integerValue}.${lastPart}`;
  }

  return removeNonLeadingMinus(parts.join(''));
}

function removeNonLeadingMinus(value: string): string {
  return value.replace(/(?!^)-/g, '');
}

export function getAvailabilityText(product: ExperimentProduct | null): string {
  const availability = product?.['availability'];
  const stock = product?.['stock'];

  if (typeof availability === 'string' || typeof availability === 'number') {
    return String(availability);
  }

  if (typeof stock === 'string' || typeof stock === 'number') {
    return String(stock);
  }

  return '';
}

export function getMissingComparisonFields(
  product: ExperimentProduct | null,
  agentKey: AgentKey,
): string[] {
  const fields = product?.comparison?.[agentKey]?.fields || {};

  return Object.keys(fields).filter((field) => fields[field] !== true);
}

export function getFoundComparisonFields(
  product: ExperimentProduct | null,
  agentKey: AgentKey,
): string[] {
  const fields = product?.comparison?.[agentKey]?.fields || {};

  return Object.keys(fields).filter((field) => fields[field] === true);
}

export function formatComparisonFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    imageUrl: 'Image',
    url: 'URL',
  };

  if (labels[field]) {
    return labels[field];
  }

  return toTitleCase(field.replace(/([a-z])([A-Z])/g, '$1 $2'));
}

export function toTitleCase(value: string): string {
  const normalized = String(value || '')
    .replace(/[_-]+/g, ' ')
    .trim();

  if (!normalized) {
    return 'AI Agent';
  }

  return normalized.replace(/\w\S*/g, (word) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}
